import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Eye, EyeOff } from "lucide-react";
import { Logo } from "../components/Logo";
import { useAuth } from "../context/AuthContext";

export default function Auth({ mode = "login" }) {
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const defaultRole = params.get("role") === "helper" ? "helper" : "client";
  const [role, setRole] = useState(defaultRole);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [fullName, setFullName] = useState("");
  const [phone, setPhone] = useState("");
  const [area, setArea] = useState("");
  const [refCode, setRefCode] = useState(params.get("ref") || "");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const cleanEmail = email.trim().toLowerCase();
      const cleanPwd = password.trim();
      let user;
      if (mode === "login") {
        user = await login(cleanEmail, cleanPwd);
      } else {
        user = await register({
          email: cleanEmail, password: cleanPwd, full_name: fullName, phone, role, area,
          referral_code: refCode || undefined,
        });
      }
      toast.success(mode === "login" ? "Connexion réussie" : "Compte créé !");
      if (user.role === "admin") navigate("/admin");
      else if (user.role === "helper") navigate("/dashboard/helper");
      else navigate("/dashboard/client");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-canvas flex flex-col">
      <header className="border-b border-[#E2E8F0] bg-white">
        <div className="max-w-6xl mx-auto px-4 md:px-8 h-16 flex items-center">
          <Link to="/"><Logo size={36} /></Link>
        </div>
      </header>
      <div className="flex-1 grid md:grid-cols-2">
        <div className="hidden md:flex flex-col justify-center bg-navy text-white p-12 relative overflow-hidden noise">
          <div className="absolute -top-20 -right-20 h-64 w-64 rounded-full bg-lime/20 blur-3xl" />
          <div className="relative">
            <span className="chip-lime">Besoin d'aide ? On est là.</span>
            <h1 className="mt-6 text-5xl font-heading font-black leading-tight">
              Rejoins le réseau <span className="text-lime">local</span> d'entraide.
            </h1>
            <p className="mt-4 text-white/80 text-lg max-w-md">
              Propose ton prix ou fais de l'argent en aidant des gens près de chez toi.
            </p>
          </div>
        </div>
        <div className="flex items-center justify-center p-6 md:p-12">
          <form onSubmit={submit} className="w-full max-w-md" data-testid="auth-form">
            <h2 className="text-3xl font-heading font-black text-navy">
              {mode === "login" ? "Connexion" : "Créer un compte"}
            </h2>
            <p className="text-[#475569] mt-1">
              {mode === "login" ? "Ravi de te revoir." : "En quelques secondes, c'est parti."}
            </p>

            {mode === "register" && (
              <>
                <div className="mt-6 grid grid-cols-2 gap-2 p-1 bg-surface rounded-full">
                  {["client", "helper"].map((r) => (
                    <button key={r} type="button" onClick={() => setRole(r)}
                      className={`py-2 rounded-full font-bold text-sm transition-all ${role === r ? "bg-navy text-white" : "text-navy"}`}
                      data-testid={`role-${r}`}
                    >
                      {r === "client" ? "Je cherche de l'aide" : "Je veux aider"}
                    </button>
                  ))}
                </div>

                <div className="mt-4">
                  <label className="text-sm font-bold text-navy">Nom complet</label>
                  <input className="input-base mt-1" value={fullName} onChange={(e) => setFullName(e.target.value)} required data-testid="input-name" />
                </div>
                <div className="mt-3 grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-sm font-bold text-navy">Téléphone</label>
                    <input className="input-base mt-1" value={phone} onChange={(e) => setPhone(e.target.value)} data-testid="input-phone" />
                  </div>
                  <div>
                    <label className="text-sm font-bold text-navy">Secteur</label>
                    <input className="input-base mt-1" placeholder="ex: Montréal" value={area} onChange={(e) => setArea(e.target.value)} data-testid="input-area" />
                  </div>
                </div>
                {(role === "helper" || role === "client") && (
                  <div className="mt-3">
                    <label className="text-sm font-bold text-navy">Code parrainage (optionnel)</label>
                    <input className="input-base mt-1 uppercase" value={refCode} onChange={(e) => setRefCode(e.target.value.toUpperCase())} data-testid="input-ref" placeholder="Ex: A4F7B2C9" />
                    <p className="text-xs text-[#475569] mt-1">
                      {role === "helper" ? "Tes 3 premières tâches débloquent 25 $ pour ton parrain et toi." : "Ta 1re tâche débloque 10 $ de crédit pour ton parrain et toi."}
                    </p>
                  </div>
                )}
              </>
            )}

            <div className="mt-4">
              <label htmlFor="auth-email" className="text-sm font-bold text-navy">Email</label>
              <input id="auth-email" name="email" type="email" autoComplete="email" aria-label="Email" className="input-base mt-1" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="input-email" />
            </div>
            <div className="mt-3">
              <label htmlFor="auth-password" className="text-sm font-bold text-navy">Mot de passe</label>
              <div className="relative mt-1">
                <input
                  id="auth-password"
                  name="password"
                  type={showPwd ? "text" : "password"}
                  autoComplete={mode === "login" ? "current-password" : "new-password"}
                  aria-label="Mot de passe"
                  className="input-base pr-12"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={6}
                  data-testid="input-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPwd((s) => !s)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#475569] hover:text-navy"
                  aria-label={showPwd ? "Masquer le mot de passe" : "Afficher le mot de passe"}
                  data-testid="toggle-password"
                >
                  {showPwd ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>

            <button type="submit" disabled={loading} className="btn-primary w-full mt-6 py-4 disabled:opacity-50" data-testid="submit-btn">
              {loading ? "Chargement..." : mode === "login" ? "Se connecter" : "Créer mon compte"}
            </button>

            <div className="text-center mt-6 text-sm text-[#475569]">
              {mode === "login" ? (
                <>Pas encore de compte ? <Link to="/register" className="text-navy font-bold hover:underline" data-testid="link-register">Inscris-toi</Link></>
              ) : (
                <>Déjà un compte ? <Link to="/login" className="text-navy font-bold hover:underline" data-testid="link-login">Connexion</Link></>
              )}
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
